/**
 * The Fusion 360 submarine, decoded.
 *
 * The third hull, and now the default one. `buildSub()` in models.ts derives a
 * boat from measurements; `buildSubModel()` in subModel.ts unpacks the Tripo
 * scan; this unpacks the CAD rebuild. ALL THREE ARE KEPT, for the reason the
 * second one gave when it arrived: a hull you can switch back to is the only
 * way to find out whether the new one is actually better. Same origin, same
 * axis, same length, same MAT slots, so every hardpoint, light and collision
 * radius lands in the same place on all three.
 *
 * What is new here is that the boat is no longer one rigid mesh plus a
 * searchlight cut out of it by part id. It arrives as ELEVEN PARTS in a tree,
 * each with the pivot it turns about and each turning about its own LOCAL Z —
 * the rig's own convention, verified across all 23 of its controls. So the
 * turret yaws, the head elevates, four control planes deflect and two bay
 * doors swing, and none of it needs a pivot coordinate written down anywhere
 * in the engine.
 *
 * It also carries baked occlusion, one byte per vertex. That rides in the uv.y
 * slot rather than in a new attribute or a texture: `vsMesh` overwrites uv.y
 * with the material tag and has never read what was in it, so the float was
 * already being paid for and already being thrown away. See the note on
 * `params2.w` in the shader for how the other meshes are kept out of it.
 */

import { MAT, VERT_FLOATS, type MeshData } from './mesh';
import {
  SUB2_DATA, SUB2_HI, SUB2_LO, SUB2_PARTS, SUB2_VERTS, type Sub2Part,
} from './subMesh2';

/** 0 paint, 1 metal, 2 glow, 3 drive — the slots the material table maps Fusion onto. */
const SLOT = [MAT.paint, MAT.metal, MAT.glow, MAT.drive];

export interface Sub2PartMesh {
  /** Node name minus the SUB_ prefix: 'HULL', 'Propeller', 'Turret_Head', … */
  name: string;
  /** Renderer slot this part is registered under. */
  slot: string;
  /** Index into the returned array, or -1 for the hull. Parents precede children. */
  parent: number;
  part: Sub2Part;
  mesh: MeshData;
}

let cached: Sub2PartMesh[] | null = null;

/** Every part, hull first, parents always before their children. */
export function buildSub2Parts(): Sub2PartMesh[] {
  return decode();
}

/** Renderer slot name for a part. The hull keeps 'sub' so nothing else moves. */
export function sub2Slot(name: string): string {
  return name === 'HULL' ? 'sub' : `sub2_${name}`;
}

function decode(): Sub2PartMesh[] {
  if (cached) return cached;

  // base64 -> bytes. `atob` is the one decoder present in both browsers and the
  // node harnesses that bundle this file.
  const raw = atob(SUB2_DATA);
  const bin = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bin[i] = raw.charCodeAt(i);

  const nv = SUB2_VERTS;
  const pos = new Int16Array(bin.buffer, 0, nv * 3);
  const nrm = new Int8Array(bin.buffer, nv * 6, nv * 3);
  const col = new Uint8Array(bin.buffer, nv * 9, nv * 3);
  const mat = new Uint8Array(bin.buffer, nv * 12, nv);
  const occ = new Uint8Array(bin.buffer, nv * 13, nv);

  const sx = (SUB2_HI[0] - SUB2_LO[0]) / 65534;
  const sy = (SUB2_HI[1] - SUB2_LO[1]) / 65534;
  const sz = (SUB2_HI[2] - SUB2_LO[2]) / 65534;

  const vertices = new Float32Array(nv * VERT_FLOATS);
  for (let k = 0; k < nv; k++) {
    const o = k * VERT_FLOATS;
    vertices[o] = SUB2_LO[0] + (pos[k * 3] + 32767) * sx;
    vertices[o + 1] = SUB2_LO[1] + (pos[k * 3 + 1] + 32767) * sy;
    vertices[o + 2] = SUB2_LO[2] + (pos[k * 3 + 2] + 32767) * sz;
    // Renormalised rather than merely scaled: quantising each component to
    // 1/127 leaves the vector slightly off unit length, and the shader's
    // specular term is sensitive enough to show it as faint mottling.
    const nx = nrm[k * 3] / 127, ny = nrm[k * 3 + 1] / 127, nz = nrm[k * 3 + 2] / 127;
    const l = Math.hypot(nx, ny, nz) || 1;
    vertices[o + 3] = nx / l;
    vertices[o + 4] = ny / l;
    vertices[o + 5] = nz / l;
    // BASE COLOUR, THREE BYTES IN ONE FLOAT.
    //
    // The vertex layout is shared by every mesh pipeline in the game and has
    // exactly one spare slot, so widening it for the boat alone was not on
    // offer. A f32 carries 24 bits exactly, which is precisely three channels:
    // 0..16,777,215 is representable to the unit, so this round-trips without
    // loss. The shader takes it apart again in `vsMesh`.
    //
    // Safe to interpolate because it never has to be: the baker emits one
    // glTF primitive per material, so all three vertices of any triangle carry
    // the same value — but it is unpacked in the VERTEX shader anyway, so even
    // a future mesh that mixed them would blend colours rather than nonsense.
    vertices[o + 6] = col[k * 3] * 65536 + col[k * 3 + 1] * 256 + col[k * 3 + 2];
    // Occlusion, in the slot the mesh pipeline has never read. The shader only
    // believes it when the instance sets params2.w.
    vertices[o + 7] = occ[k] / 255;
    vertices[o + 8] = SLOT[mat[k]] ?? MAT.metal;
  }

  // Zigzag varint deltas, one stream across every part in table order. The
  // indices are already LOCAL to their part, so a part is a straight slice of
  // the vertex array with no rebasing — the delta from one part's last index
  // to the next part's first is simply a large negative one, which zigzag
  // costs the same as a large positive.
  let p = nv * 14;
  let prev = 0;
  const readIndices = (count: number) => {
    const out = new Uint32Array(count);
    for (let i = 0; i < count; i++) {
      let shift = 0, z = 0, b = 0;
      do {
        b = bin[p++];
        z |= (b & 127) << shift;
        shift += 7;
      } while (b & 128);
      prev += (z >>> 1) ^ -(z & 1);
      out[i] = prev;
    }
    return out;
  };

  cached = SUB2_PARTS.map((part) => ({
    name: part.name,
    slot: sub2Slot(part.name),
    parent: part.parent,
    part,
    mesh: {
      vertices: vertices.subarray(
        part.vo * VERT_FLOATS, (part.vo + part.vc) * VERT_FLOATS,
      ) as Float32Array<ArrayBuffer>,
      indices: readIndices(part.tc * 3),
    },
  }));
  // The parts are emitted parents-first by the baker; assert it rather than
  // trust it, because a forward reference here would silently compose a child
  // against last frame's parent.
  for (let i = 0; i < cached.length; i++) {
    if (cached[i].parent >= i) throw new Error(`sub2: part ${i} references parent ${cached[i].parent}`);
  }
  return cached;
}

/**
 * The part's local transform, with `angle` applied about its LOCAL Z.
 *
 * Column-major 4x4, the layout `compose()` and the renderer already use.
 * Written out rather than composed from helpers because the rest rotation is a
 * general 3x3 from the rig, not an Euler triple — going through `compose`
 * would mean decomposing it to angles and rebuilding it, twice per part per
 * frame, to arrive back where we started.
 */
/**
 * Joints that turn about their local Y instead of their local Z.
 *
 * THE RIG'S "EVERY CONTROL TURNS ABOUT LOCAL Z" IS TRUE OF THE CONTROLS AND
 * NOT OF THESE TWO NODES. Measured, not assumed — `Turret_Head` and
 * `Lamp_Head` both carry the basis
 *
 *     local X -> (1, 0, 0)    local Y -> (0, 0, 1)    local Z -> (0, -1, 0)
 *
 * so their local Z is the boat's VERTICAL. Turning about it traverses the head
 * left and right across a side-on camera: the barrel swings toward the viewer
 * and foreshortens, which is why it read as the gun spinning about its own
 * length. Local Y is the one that maps to the lateral axis, and rotating about
 * the lateral axis is what elevation is.
 *
 * The lamp has been doing this for as long as it has had a swivel. Nobody
 * caught it because a searchlight housing is nearly a solid of revolution and
 * yawing one looks like very little; a gun barrel is not, and does not.
 *
 * Everything else — prop, doors, control planes, every arm joint — really is
 * local Z and is untouched.
 */
export function sub2PitchJoint(name: string): boolean {
  return name === 'Turret_Head' || name === 'Lamp_Head';
}

export function sub2Local(
  out: Float32Array, part: Sub2Part, angle: number, linkScale = 1,
  aboutY = false,
): Float32Array {
  const r = part.r;
  const c = Math.cos(angle), s = Math.sin(angle);
  if (aboutY) {
    // R = rest * Ry(angle), whose columns are (c,0,-s), (0,1,0), (s,0,c) — so
    // it mixes the OUTER two columns and leaves the middle one alone, the
    // mirror of what Rz does below.
    out[0] = r[0] * c - r[6] * s;
    out[1] = r[1] * c - r[7] * s;
    out[2] = r[2] * c - r[8] * s;
    out[3] = 0;
    out[4] = r[3];
    out[5] = r[4];
    out[6] = r[5];
    out[7] = 0;
    out[8] = r[0] * s + r[6] * c;
    out[9] = r[1] * s + r[7] * c;
    out[10] = r[2] * s + r[8] * c;
    out[11] = 0;
    out[12] = part.t[0] * linkScale;
    out[13] = part.t[1] * linkScale;
    out[14] = part.t[2] * linkScale;
    out[15] = 1;
    return out;
  }
  // R = rest * Rz(angle). Rz only mixes the first two columns.
  //
  // The BASIS is never scaled. Scaling it would propagate down the tree — a
  // child's matrix is its parent's times its own — and because the arm's joints
  // are not axis-aligned, a non-uniform parent scale arrives at the child as
  // SHEAR. Only the translation is scaled, which is what slides a segment
  // outward along the axis its parent extends in.
  out[0] = r[0] * c + r[3] * s;
  out[1] = r[1] * c + r[4] * s;
  out[2] = r[2] * c + r[5] * s;
  out[3] = 0;
  out[4] = r[3] * c - r[0] * s;
  out[5] = r[4] * c - r[1] * s;
  out[6] = r[5] * c - r[2] * s;
  out[7] = 0;
  out[8] = r[6];
  out[9] = r[7];
  out[10] = r[8];
  out[11] = 0;
  out[12] = part.t[0] * linkScale;
  out[13] = part.t[1] * linkScale;
  out[14] = part.t[2] * linkScale;
  out[15] = 1;
  return out;
}

/**
 * `out = m * scale(1, k, 1)` — stretch a part's MESH along its own local Y.
 *
 * Every link in the manipulator has its child offset along local -Y, so local Y
 * is the direction each segment runs in. Stretching it lengthens the segment
 * without thickening it, which is the difference between a boom that telescopes
 * and one that is simply drawn bigger.
 *
 * Applied at DRAW time, to a copy, and deliberately never fed back into the
 * hierarchy — see `sub2Local` for why a scaled basis cannot be allowed to
 * propagate. Post-multiplying by a scale touches one column, so this is four
 * multiplies rather than a matrix product.
 */
export function sub2Stretch<T extends Float32Array>(out: T, m: Float32Array, k: number): T {
  out.set(m);
  out[4] = m[4] * k;
  out[5] = m[5] * k;
  out[6] = m[6] * k;
  return out;
}

/** `out = a * b`, column-major. */
export function mat4Mul(out: Float32Array, a: Float32Array, b: Float32Array): Float32Array {
  for (let c = 0; c < 4; c++) {
    const b0 = b[c * 4], b1 = b[c * 4 + 1], b2 = b[c * 4 + 2], b3 = b[c * 4 + 3];
    out[c * 4] = a[0] * b0 + a[4] * b1 + a[8] * b2 + a[12] * b3;
    out[c * 4 + 1] = a[1] * b0 + a[5] * b1 + a[9] * b2 + a[13] * b3;
    out[c * 4 + 2] = a[2] * b0 + a[6] * b1 + a[10] * b2 + a[14] * b3;
    out[c * 4 + 3] = a[3] * b0 + a[7] * b1 + a[11] * b2 + a[15] * b3;
  }
  return out;
}

